import { IntegrationLinkKind } from '../types';

/**
 * Koppel-intent van de koppelpagina (partnerkoppeling, bv. Praktijkdata).
 *
 * Staat in sessionStorage: blijft bewaard bij navigatie en inloggen in hetzelfde
 * tabblad (ook bij verversen) en verdwijnt met het tabblad. Bevat het token uit
 * de koppellink en de claim die alleen deze browser kreeg; nooit loggen en
 * nooit in een url zetten.
 */
export const KOPPEL_PATH = '/koppelen';

const STORAGE_KEY = 'notizy.koppelIntent';

// Na het openen is er tien minuten om in te loggen en te bevestigen. Zonder
// expires_at (open nog niet gelukt) geldt de openingstijd plus deze marge.
const FALLBACK_TTL_MS = 10 * 60 * 1000;

export interface KoppelIntent {
    token: string;
    /** Na een geslaagde aanroep van links/open */
    claim?: string;
    kind?: IntegrationLinkKind;
    partnerName?: string;
    /** ISO, uit links/open */
    expiresAt?: string;
    openedAt: string;
}

export const readKoppelIntent = (): KoppelIntent | null => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed.token !== 'string' || !parsed.token) return null;
        return {
            token: parsed.token,
            claim: typeof parsed.claim === 'string' && parsed.claim ? parsed.claim : undefined,
            kind: parsed.kind === 'organization' || parsed.kind === 'user' ? parsed.kind : undefined,
            partnerName: typeof parsed.partnerName === 'string' ? parsed.partnerName : undefined,
            expiresAt: typeof parsed.expiresAt === 'string' ? parsed.expiresAt : undefined,
            openedAt: typeof parsed.openedAt === 'string' ? parsed.openedAt : new Date().toISOString(),
        };
    } catch {
        return null;
    }
};

export const writeKoppelIntent = (intent: KoppelIntent): void => {
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
    } catch {
        // Zonder sessionStorage werkt de koppelpagina alleen zonder inloggen tussendoor
    }
};

export const clearKoppelIntent = (): void => {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        // Negeren
    }
};

const isExpired = (intent: KoppelIntent, now = Date.now()): boolean => {
    const expiresAt = Date.parse(intent.expiresAt ?? '');
    if (!Number.isNaN(expiresAt)) return expiresAt <= now;
    const openedAt = Date.parse(intent.openedAt);
    return Number.isNaN(openedAt) || openedAt + FALLBACK_TTL_MS <= now;
};

/**
 * De intent als er nog een koppeling af te maken is. Een verlopen intent wordt
 * opgeruimd, zodat een latere login niet meer naar de koppelpagina gaat.
 */
export const getActiveKoppelIntent = (): KoppelIntent | null => {
    const intent = readKoppelIntent();
    if (!intent) return null;
    if (isExpired(intent)) {
        clearKoppelIntent();
        return null;
    }
    return intent;
};

/** Waar de app na het inloggen heen gaat: terug naar de koppelpagina als die wacht. */
export const getPostLoginPath = (): string => (getActiveKoppelIntent() ? KOPPEL_PATH : '/recordings');
